import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { User } from '../../models/user';
import { ProfileMessages } from '../../intl/ProfileMessages';
import { DqText } from '../dq-text';
import { GenericMessages } from '../../intl/GenericMessages';
import { Roles } from '@dataquiver/stoneage-roles';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { Input } from '../reusable-components/Input';
import { InputSelect } from '../reusable-components/InputSelect';

interface EditUserModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** Triggers the request to close the modal and submit the save if applicable. */
  onSave: (role?: number) => void;
  /** The user being updated. */
  user?: User;
  /** The user's organization's name. */
  userOrganizationName?: string;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onSave, user, userOrganizationName }) => {
  const { formatMessage } = useIntl();
  const [role, setRole] = useState<number | undefined>(user?.role);
  const [roleError, setRoleError] = useState<boolean>(false);

  const handleSubmit = React.useCallback(() => {
    // Validate values
    if (!role) {
      setRoleError(true);
      return;
    }

    onSave(role);
  }, [onSave, role]);

  const handleCancel = React.useCallback(() => {
    onSave(undefined);
  }, [onSave]);

  const availableRoles = React.useMemo(
    () => [
      { id: Roles.roles.STONEAGE_ADMIN, name: Roles.getPrettyPrint(Roles.roles.STONEAGE_ADMIN) },
      { id: Roles.roles.STONEAGE_DEV, name: Roles.getPrettyPrint(Roles.roles.STONEAGE_DEV) },
    ],
    [],
  );

  const availableRolesSelectOptions = React.useMemo(() => {
    return availableRoles.map(role => {
      return {
        value: role.id,
        label: role.name,
      };
    });
  }, [availableRoles]);

  const resolvedOrganizationName = React.useMemo(
    () => userOrganizationName ?? formatMessage(GenericMessages.unknownPlaceholder),
    [formatMessage, userOrganizationName],
  );

  const submitButton = React.useMemo(() => {
    return {
      label: formatMessage(GenericMessages.saveButtonLabel),
      onClick: handleSubmit,
    };
  }, [formatMessage, handleSubmit]);

  const cancelButton = React.useMemo(() => {
    return {
      label: formatMessage(GenericMessages.cancelButtonLabel),
      onClick: handleCancel,
    };
  }, [formatMessage, handleCancel]);

  return (
    <AppModal
      isOpen={isOpen}
      title={formatMessage(ProfileMessages.updateUserRoleHeader)}
      onClose={handleCancel}
      primaryButton={submitButton}
      secondaryButton={cancelButton}
    >
      <Input
        disabled={true}
        label={formatMessage(GenericMessages.nameLabel)}
        value={`${user?.given_name} ${user?.family_name}`}
        id="edit-user-name"
      />
      <Input
        disabled={true}
        label={formatMessage(GenericMessages.emailColumnLabel)}
        value={user?.email}
        id="edit-user-email"
      />
      <Input
        disabled={true}
        label={formatMessage(GenericMessages.organizationColumnLabel)}
        value={resolvedOrganizationName}
        id="edit-user-organization-name"
      />
      <InputSelect<number>
        label={formatMessage(ProfileMessages.role)}
        value={role ?? user?.role}
        onChange={roleId => setRole(roleId)}
        placeholder={formatMessage(ProfileMessages.setUserRolePlaceholder)}
        options={availableRolesSelectOptions}
        id="edit-user-select-role"
      />
      {roleError && (
        <DqText color="danger" variant="body-strong">
          {formatMessage(ProfileMessages.roleRequiredLabel)}
        </DqText>
      )}
    </AppModal>
  );
};
